/* Shadow Box Builder — Hooyah Woodworks
 * Vanilla JS. No dependencies. Pointer events (mouse + touch unified).
 * Catalog: Builder Catalog collection products.json (paginated, cached in sessionStorage).
 * Case: CSS-rendered from wood swatch texture (or a real photo if the block has one).
 *       Photo mode: the customer's chosen felt color paints the interior region over the photo.
 * Cart: POST /cart/add.js — case variant + one line per distinct award variant + optional flag.
 * Ceremony date rides the case line as a property (required, or explicit 'no ceremony').
 *
 * SCALE: placed awards are sized in real inches against the interior of the case.
 * See PICTURED_* and SCALE_BY_CAT below — the photographed case is always 16in x 22in outer.
 */
(function () {
  'use strict';
  const CFG = window.SBB_CONFIG;
  if (!CFG) return;
  const T = CFG.text || {};
  const FLAG = CFG.flag || {};
 
  const $ = (s, el) => (el || document).querySelector(s);
  const $$ = (s, el) => Array.from((el || document).querySelectorAll(s));
 
  const FRAME_IN = 1.25; // visual frame thickness in inches for rendered cases
 
  /* ── Scale reference ───────────────────────────────────────────────
   * The case PHOTO on the stage always shows a 16in x 22in box, whatever size the
   * customer picked. Awards must be scaled against the interior of that box.
   *
   * BUG THIS REPLACES: photo mode used the per-block setting `interior_width_inches`,
   * whose default is 16 — the OUTER width. The interior is the felt area inside the
   * frame, 16 - 2 x 1.25 = 13.5in. Dividing by 16 instead of 13.5 overstated
   * inches-per-pixel by 18%, so every medal, badge and patch rendered ~16% smaller
   * than life size. Deriving the number here makes the scale correct and immune to
   * that setting being left at its default.
   *
   * IF A DIFFERENT CASE IS EVER PHOTOGRAPHED, UPDATE PICTURED_W_IN / PICTURED_H_IN.
   * Nothing else reads them and nothing will warn you.
   * ─────────────────────────────────────────────────────────────── */
  const PICTURED_W_IN = 16;
  const PICTURED_H_IN = 22;
  const PICTURED_INTERIOR_W_IN = PICTURED_W_IN - 2 * FRAME_IN; // 13.5in
 
  /* Award size on the case, as a multiple of true life size. 1 = exact. Above 1 is a
   * deliberate legibility choice, not a correction. Set in the theme editor:
   * Shadow Box Builder > "Award size on the case". */
  const ITEM_SCALE = Number(CFG.itemScale) > 0 ? Number(CFG.itemScale) : 1;
 
  /* Per-category exceptions to ITEM_SCALE. A ribbon bar is 1 3/8in wide by regulation
   * and racks up in tight rows — enlarging it past life size makes a rack read wrong
   * and mis-sets the customer's expectation of how much space it takes. Ribbons stay
   * at 1. Any category not listed follows ITEM_SCALE. */
  const SCALE_BY_CAT = Object.assign({ ribbon: 1 }, CFG.itemScaleByCat || {});
  function catScale(cat) {
    const v = Number(SCALE_BY_CAT[cat]);
    return v > 0 ? v : ITEM_SCALE;
  }
 
  const state = {
    catalog: [],
    byVariant: new Map(),
    wood: null, size: null, felt: null,
    placed: [],             // {uid, kind:'item'|'photo', variantId?, wIn?, hIn?, xPct, yPct, z}
    zTop: 10,
    activeCat: 'medal',
    activeBranch: '',
    search: '',
    flag: null,             // 'add' | 'own' | null
    builderId: 'HW-' + Date.now().toString(36).toUpperCase()
  };
 
  const els = {
    root: $('#sbb-root'), stage: $('#sbb-stage'), caseImg: $('#sbb-case-img'),
    caseRender: $('#sbb-case-render'), caseFrame: $('#sbb-case-frame'), caseFelt: $('#sbb-case-felt'),
    interior: $('#sbb-interior'), catalog: $('#sbb-catalog'),
    woodGrid: $('#sbb-wood-grid'), sizeRow: $('#sbb-size-row'), feltRow: $('#sbb-felt-row'),
    lockNote: $('#sbb-lock-note'), stageHint: $('#sbb-stage-hint'),
    trash: $('#sbb-trash'), total: $('#sbb-total'), count: $('#sbb-count'),
    atc: $('#sbb-atc'), atcPrice: $('#sbb-atc-price'), review: $('#sbb-review'),
    search: $('#sbb-search'),
    flagChoices: $('#sbb-flag-choices'), plate: $('#sbb-plate'),
    photoW: $('#sbb-photo-w'), photoH: $('#sbb-photo-h'), photoAdd: $('#sbb-photo-add'),
    photoFile: $('#sbb-photo-file'),
    date: $('#sbb-date'), noDate: $('#sbb-no-date'), dateHint: $('#sbb-date-hint')
  };
 
  const money = (n) => '$' + (Math.round(n * 100) / 100).toFixed(2);
 
  const woods = () => [...new Map(CFG.cases.map(c => [c.wood, c])).values()];
  const sizesFor = (wood) => CFG.cases.filter(c => c.wood === wood);
  const activeCase = () => CFG.cases.find(c => c.wood === state.wood && c.sizeLabel === state.size) || null;
  const caseComplete = () => !!(state.wood && state.size && state.felt);
  /* Date gate: satisfied by a chosen date OR the explicit no-ceremony box.
   * If the date UI is absent from the DOM (template mismatch), pass — never brick the builder. */
  const dateOk = () => {
    if (!els.date && !els.noDate) return true;
    return !!((els.noDate && els.noDate.checked) || (els.date && els.date.value));
  };
  const ceremonyDateText = () => (els.noDate && els.noDate.checked)
    ? (T.dateNone || 'No ceremony — standard schedule')
    : ((els.date && els.date.value) || '');
 
  /* ── Catalog ──────────────────────────── */
  async function loadCatalog() {
    const CACHE_KEY = 'sbb-catalog-v3'; // v3: includes ribbons + rank/rate
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) { state.catalog = JSON.parse(cached); indexCatalog(); renderCatalog(); }
    } catch (e) { /* private mode */ }
 
    // Always refetch (stale-while-revalidate) so price / catalog edits appear on the next load
    const items = [];
    for (let page = 1; page <= 20; page++) {   // up to 5000 products
      let res;
      try { res = await fetch(`${CFG.catalogUrl}?limit=250&page=${page}`); } catch (e) { break; }
      if (!res.ok) break;
      const data = await res.json();
      const products = data.products || [];
      for (const p of products) {
        const v = p.variants && p.variants[0];
        if (!v) continue;
        const tags = (Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map(t => t.trim()));
        const catTag = tags.find(t => t.indexOf('builder:') === 0);
        items.push({
          variantId: v.id,
          title: p.title.replace(/^(Full Size Medal|Army Badge|Navy Badge|Badge|Air Force Badge|Patch|Ribbon Unit|Army Chevron|Marine Corps Chevron|.*Embroidered Rank|.*Rating Badge|.*Rate):\s*/i, ''),
          fullTitle: p.title,
          price: parseFloat(v.price),
          img: (p.images && p.images[0] && p.images[0].src) || '',
          cat: catTag ? catTag.split(':')[1] : 'medal',
          branches: tags.filter(t => ['Army','Navy','Air Force','Marine Corps','Coast Guard','Space Force'].includes(t)),
          sku: v.sku || ''
        });
      }
      if (products.length < 250) break;
    }
    if (!items.length) return; // fetch failed — keep the cached render
    state.catalog = items;
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(items)); } catch (e) { /* quota */ }
    indexCatalog();
    renderCatalog();
    updateTotals(); // reflect any changed prices in the running total/review
  }
 
  function indexCatalog() {
    state.byVariant.clear();
    for (const it of state.catalog) state.byVariant.set(String(it.variantId), it);
  }
 
  function thumbUrl(src, size) {
    if (!src) return '';
    return src.replace(/(\.[a-z]{3,4})(\?|$)/i, `_${size}x$1$2`);
  }
 
  /* Strip the white studio background in-browser (border flood-fill; keeps interior white). */
  const _bgCache = new Map();
  function removeWhiteBg(src) {
    if (_bgCache.has(src)) return _bgCache.get(src);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const MAX = 420;
          let w = img.naturalWidth, h = img.naturalHeight;
          const sc = Math.min(1, MAX / Math.max(w, h));
          w = Math.max(1, Math.round(w * sc)); h = Math.max(1, Math.round(h * sc));
          const c = document.createElement('canvas'); c.width = w; c.height = h;
          const ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const id = ctx.getImageData(0, 0, w, h), data = id.data;
          const TH = 238, n = w * h, seen = new Uint8Array(n), stack = [];
          const seed = (x, y) => { if (x >= 0 && y >= 0 && x < w && y < h) stack.push(y * w + x); };
          for (let x = 0; x < w; x++) { seed(x, 0); seed(x, h - 1); }
          for (let y = 0; y < h; y++) { seed(0, y); seed(w - 1, y); }
          while (stack.length) {
            const idx = stack.pop();
            if (seen[idx]) continue; seen[idx] = 1;
            const i = idx * 4;
            if (data[i] < TH || data[i + 1] < TH || data[i + 2] < TH) continue;
            data[i + 3] = 0;
            const x = idx % w, y = (idx / w) | 0;
            seed(x + 1, y); seed(x - 1, y); seed(x, y + 1); seed(x, y - 1);
          }
          for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            const idx = y * w + x, i = idx * 4;
            if (data[i + 3] === 0) continue;
            const mn = Math.min(data[i], data[i + 1], data[i + 2]);
            if (mn < 210) continue;
            const edge = (x > 0 && data[(idx - 1) * 4 + 3] === 0) || (x < w - 1 && data[(idx + 1) * 4 + 3] === 0) ||
                         (y > 0 && data[(idx - w) * 4 + 3] === 0) || (y < h - 1 && data[(idx + w) * 4 + 3] === 0);
            if (edge) data[i + 3] = Math.round(255 * (1 - (mn - 210) / 45));
          }
          ctx.putImageData(id, 0, 0);
          resolve(c.toDataURL('image/png'));
        } catch (e) { resolve(src); }
      };
      img.onerror = () => resolve(src);
      img.src = src;
    });
    _bgCache.set(src, p);
    return p;
  }
 
  function renderCatalog() {
    const q = state.search.toLowerCase();
    const list = state.catalog.filter(it =>
      it.cat === state.activeCat &&
      (!state.activeBranch || it.branches.includes(state.activeBranch) || it.branches.length === 0) &&
      (!q || it.fullTitle.toLowerCase().includes(q))
    );
    list.sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base', numeric: true }));
    const frag = document.createDocumentFragment();
    const wrap = document.createElement('div');
    wrap.className = 'sbb__catalog-grid';
    list.slice(0, 2500).forEach(it => {
      const card = document.createElement('button');
      card.className = 'sbb__item';
      card.type = 'button';
      card.dataset.variant = it.variantId;
      card.innerHTML =
        `<img loading="lazy" src="${thumbUrl(it.img, 160)}" alt="">` +
        `<span class="sbb__item-name">${it.title}</span>` +
        `<span class="sbb__item-price">${money(it.price)}</span>`;
      card.addEventListener('click', () => placeItem(it));
      wrap.appendChild(card);
    });
    frag.appendChild(wrap);
    els.catalog.innerHTML = list.length ? '' : `<div class="sbb__loading">${T.noMatches || 'No matches.'}</div>`;
    els.catalog.appendChild(frag);
  }
 
  /* ── Case selection: wood → size → felt (all three required) ─ */
  function renderCaseOptions() {
    els.woodGrid.innerHTML = '';
    woods().forEach(c => {
      const b = document.createElement('button');
      b.className = 'sbb__case-card' + (c.wood === state.wood ? ' is-active' : '');
      b.type = 'button';
      b.innerHTML = `<img src="${c.woodImage}" alt=""><span>${c.wood}</span>`;
      b.addEventListener('click', () => { state.wood = c.wood; ensureSizeCleared(); applyCase(); });
      els.woodGrid.appendChild(b);
    });
 
    els.sizeRow.innerHTML = '';
    if (state.wood) {
      sizesFor(state.wood).forEach(c => {
        const b = document.createElement('button');
        b.className = 'sbb__pill' + (c.sizeLabel === state.size ? ' is-active' : '');
        b.type = 'button';
        b.innerHTML = `${c.sizeLabel} <em>${money(c.price)}</em>`;
        b.addEventListener('click', () => { state.size = c.sizeLabel; applyCase(); });
        els.sizeRow.appendChild(b);
      });
    } else {
      els.sizeRow.innerHTML = `<p class="sbb__hint">${T.pickWoodFirst || 'Pick a wood first.'}</p>`;
    }
 
    els.feltRow.innerHTML = '';
    (CFG.felts || []).forEach(f => {
      const b = document.createElement('button');
      b.className = 'sbb__felt' + (f.name === state.felt ? ' is-active' : '');
      b.type = 'button';
      b.title = f.name;
      b.style.background = f.color;
      b.addEventListener('click', () => { state.felt = f.name; applyCase(); });
      els.feltRow.appendChild(b);
    });
  }
 
  function ensureSizeCleared() {
    // when wood changes, keep size only if it still exists for that wood
    const sizes = sizesFor(state.wood);
    if (!sizes.find(c => c.sizeLabel === state.size)) state.size = null;
  }
 
  function feltColor() {
    const f = (CFG.felts || []).find(x => x.name === state.felt);
    return f ? f.color : '#2a2a2c';
  }
 
  function applyCase() {
    renderCaseOptions();
    const c = activeCase();
    if (els.lockNote) els.lockNote.hidden = true;
    if (!c) { if (els.stageHint) els.stageHint.hidden = false; return; }  // need wood+size to render
    if (els.stageHint) els.stageHint.hidden = true;
    if (c.image) {
      els.caseImg.src = c.image;
      els.caseImg.hidden = false;
      els.caseRender.hidden = true;
      Object.assign(els.interior.style, { left: c.interior.x + '%', top: c.interior.y + '%', width: c.interior.w + '%', height: c.interior.h + '%' });
      // Photo mode: paint the interior with the customer's chosen felt so the color
      // selection is visible over the photographed case. Depth shading (radial falloff
      // + inset shadow) keeps it reading as recessed fabric rather than a flat patch.
      // No felt chosen yet → leave the photo untouched.
      if (state.felt) {
        els.interior.style.background =
          'radial-gradient(120% 90% at 50% 35%, rgba(255,255,255,.05), rgba(0,0,0,.22)), ' + feltColor();
        els.interior.style.boxShadow = 'inset 0 0 42px rgba(0,0,0,.38)';
      } else {
        els.interior.style.background = 'transparent';
        els.interior.style.boxShadow = 'none';
      }
    } else {
      const fx = FRAME_IN / c.widthIn * 100, fy = FRAME_IN / c.heightIn * 100;
      els.caseImg.hidden = true;
      els.caseRender.hidden = false;
      els.caseRender.style.aspectRatio = c.widthIn + ' / ' + c.heightIn;
      els.caseFrame.style.backgroundImage = `url(${c.woodImage})`;
      Object.assign(els.caseFelt.style, { left: fx + '%', top: fy + '%', width: (100 - 2 * fx) + '%', height: (100 - 2 * fy) + '%', background: feltColor() });
      Object.assign(els.interior.style, { left: fx + '%', top: fy + '%', width: (100 - 2 * fx) + '%', height: (100 - 2 * fy) + '%' });
      // Render mode paints felt on its own layer — keep the interior transparent.
      els.interior.style.background = 'transparent';
      els.interior.style.boxShadow = 'none';
    }
    requestAnimationFrame(resizePlaced);
    updateTotals();
    if (caseComplete()) gotoStep('awards');   // only advance once all three chosen
  }
 
  /* Real-world width, in inches, of the felt area the awards sit on.
   * Photo mode: the photographed case is always PICTURED_W_IN wide, so the interior
   * is that less the frame on both sides. The per-block `interior_width_inches`
   * setting is deliberately NOT used — its default is the outer width, which made
   * every award render undersized. See the PICTURED_* block at the top. */
  function interiorWidthInches(c) {
    if (!c) return PICTURED_INTERIOR_W_IN;
    if (c.image) return PICTURED_INTERIOR_W_IN;
    return c.widthIn - 2 * FRAME_IN;
  }
  function inchesPerPx() {
    const c = activeCase();
    const pxW = els.interior.getBoundingClientRect().width || 1;
    return interiorWidthInches(c) / pxW;
  }
 
  /* ── Placement engine ─────────────────── */
  function itemPxWidth(cat) {
    const c = activeCase();
    if (!c) return 60;
    const inches = (CFG.itemWidthInches[cat] || 1.5) * catScale(cat);
    return Math.max(18, inches / inchesPerPx());
  }
 
  function placeItem(it) {
    if (!activeCase()) { gotoStep('case'); return; }
    const uid = 'u' + (++state.zTop) + Date.now().toString(36);
    const rec = { uid, kind: 'item', variantId: it.variantId, xPct: 42 + Math.random() * 16, yPct: 38 + Math.random() * 20, z: ++state.zTop };
    state.placed.push(rec);
    els.interior.appendChild(buildPlacedEl(rec));
    updateTotals();
  }
 
  function addPhoto() {
    if (!activeCase()) { gotoStep('case'); return; }
    const wIn = Math.max(1, Math.min(30, parseFloat(els.photoW && els.photoW.value) || 4));
    const hIn = Math.max(1, Math.min(30, parseFloat(els.photoH && els.photoH.value) || 6));
    const uid = 'p' + (++state.zTop) + Date.now().toString(36);
    const rec = { uid, kind: 'photo', wIn, hIn, xPct: 42 + Math.random() * 12, yPct: 40 + Math.random() * 12, z: ++state.zTop };
    // Attach the customer's picture, if one was chosen (uploads with the order at add-to-cart)
    const f = els.photoFile && els.photoFile.files && els.photoFile.files[0];
    if (f) {
      if (f.size > 18 * 1024 * 1024) { alert('That photo is over 18 MB — please choose a smaller file.'); return; }
      rec.file = f;
      rec.fileName = f.name;
      rec.fileUrl = URL.createObjectURL(f);
      els.photoFile.value = '';
    }
    state.placed.push(rec);
    els.interior.appendChild(buildPlacedEl(rec));
    updateTotals();
  }
 
  const PHOTO_SVG = '<svg viewBox="0 0 80 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true"><rect width="80" height="100" fill="none"/><circle cx="40" cy="38" r="17" fill="#7d7469"/><path d="M12 92c0-17 12.5-27 28-27s28 10 28 27z" fill="#7d7469"/></svg>';
 
  function buildPlacedEl(rec) {
    const el = document.createElement('div');
    el.id = rec.uid;
    el.style.zIndex = rec.z;
    el.style.left = rec.xPct + '%';
    el.style.top = rec.yPct + '%';
    if (rec.kind === 'photo') {
      el.className = 'sbb__placed sbb__placed--photo';
      const scale = 1 / inchesPerPx();
      el.style.width = (rec.wIn * scale) + 'px';
      el.style.height = (rec.hIn * scale) + 'px';
      if (rec.fileUrl) {
        // Customer attached a real picture — show it in the frame
        const img = document.createElement('img');
        img.className = 'sbb__photoimg';
        img.draggable = false; img.alt = '';
        img.src = rec.fileUrl;
        el.appendChild(img);
        el.title = `Your photo ${rec.wIn}×${rec.hIn} in — drag to position, drag off to remove`;
      } else {
        el.innerHTML = `<div class="sbb__photoph">${PHOTO_SVG}<span class="sbb__photoph-cap">${rec.wIn} × ${rec.hIn} in<br><small>your photo</small></span></div>`;
        el.title = `Photo placeholder ${rec.wIn}×${rec.hIn} in — drag to position, drag off to remove`;
      }
    } else {
      const it = state.byVariant.get(String(rec.variantId));
      el.className = 'sbb__placed';
      el.style.width = itemPxWidth(it ? it.cat : 'medal') + 'px';
      const img = document.createElement('img');
      img.draggable = false; img.alt = it ? it.title : '';
      if (it) { img.src = thumbUrl(it.img, 360); removeWhiteBg(thumbUrl(it.img, 420)).then(u => { img.src = u; }); }
      el.appendChild(img);
      el.title = (it ? it.title : '') + ' — drag to position, drag off case to remove';
    }
    attachDrag(el, rec);
    return el;
  }
 
  function resizePlaced() {
    for (const rec of state.placed) {
      const el = document.getElementById(rec.uid);
      if (!el) continue;
      if (rec.kind === 'photo') {
        const scale = 1 / inchesPerPx();
        el.style.width = (rec.wIn * scale) + 'px';
        el.style.height = (rec.hIn * scale) + 'px';
      } else {
        const it = state.byVariant.get(String(rec.variantId));
        if (it) el.style.width = itemPxWidth(it.cat) + 'px';
      }
    }
  }
 
  function attachDrag(el, rec) {
    let startX, startY, origX, origY, dragging = false;
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      dragging = true;
      el.setPointerCapture(e.pointerId);
      rec.z = ++state.zTop;
      el.style.zIndex = rec.z;
      startX = e.clientX; startY = e.clientY;
      const r = els.interior.getBoundingClientRect();
      origX = rec.xPct / 100 * r.width;
      origY = rec.yPct / 100 * r.height;
      els.trash.hidden = false;
      el.classList.add('is-dragging');
    });
    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const r = els.interior.getBoundingClientRect();
      const nx = origX + (e.clientX - startX);
      const ny = origY + (e.clientY - startY);
      rec.xPct = Math.max(-8, Math.min(104, nx / r.width * 100));
      rec.yPct = Math.max(-8, Math.min(104, ny / r.height * 100));
      el.style.left = rec.xPct + '%';
      el.style.top = rec.yPct + '%';
      const t = els.trash.getBoundingClientRect();
      const over = e.clientX > t.left && e.clientX < t.right && e.clientY > t.top && e.clientY < t.bottom;
      els.trash.classList.toggle('is-hot', over);
      el.classList.toggle('is-doomed', over);
    });
    el.addEventListener('pointerup', (e) => {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('is-dragging');
      els.trash.hidden = true;
      const t = els.trash.getBoundingClientRect();
      const over = e.clientX > t.left && e.clientX < t.right && e.clientY > t.top && e.clientY < t.bottom;
      const out = rec.xPct < -4 || rec.xPct > 102 || rec.yPct < -4 || rec.yPct > 102;
      if (over || out) removePlaced(rec.uid);
      updateTotals();
    });
    el.tabIndex = 0;
    el.addEventListener('keydown', (e) => {
      const step = e.shiftKey ? 2 : 0.5;
      if (e.key === 'ArrowLeft') rec.xPct -= step;
      else if (e.key === 'ArrowRight') rec.xPct += step;
      else if (e.key === 'ArrowUp') rec.yPct -= step;
      else if (e.key === 'ArrowDown') rec.yPct += step;
      else if (e.key === 'Delete' || e.key === 'Backspace') { removePlaced(rec.uid); return; }
      else return;
      e.preventDefault();
      el.style.left = rec.xPct + '%';
      el.style.top = rec.yPct + '%';
    });
  }
 
  function removePlaced(uid) {
    // Release the preview blob before dropping the record — nothing else holds a reference,
    // so without this every add/remove cycle leaks the full-size image for the session.
    const gone = state.placed.find(p => p.uid === uid);
    if (gone && gone.fileUrl) { try { URL.revokeObjectURL(gone.fileUrl); } catch (e) { /* ignore */ } }
    state.placed = state.placed.filter(p => p.uid !== uid);
    const el = document.getElementById(uid);
    if (el) el.remove();
    updateTotals();
  }
 
  /* ── Flag step ────────────────────────── */
  function renderFlagButtons() {
    if (!els.flagChoices) return;
    const addBtn = els.flagChoices.querySelector('[data-flag="add"]');
    const ownBtn = els.flagChoices.querySelector('[data-flag="own"]');
    if (addBtn) {
      addBtn.textContent = (FLAG.addLabel || 'Add a folded flag') + ' (+' + money(FLAG.price || 0) + ')';
      addBtn.classList.toggle('is-active', state.flag === 'add');
    }
    if (ownBtn) {
      ownBtn.textContent = FLAG.ownLabel || 'I have my own flag';
      ownBtn.classList.toggle('is-active', state.flag === 'own');
    }
  }
 
  /* ── Totals / review ────────────────────── */
  function currentTotals() {
    const c = activeCase();
    let total = c ? Number(c.price) : 0;
    const counts = new Map();
    for (const p of state.placed) {
      if (p.kind !== 'item') continue;
      const it = state.byVariant.get(String(p.variantId));
      if (!it) continue;
      total += it.price;
      counts.set(String(p.variantId), (counts.get(String(p.variantId)) || 0) + 1);
    }
    if (state.flag === 'add') total += Number(FLAG.price || 0);
    return { total, counts, casePrice: c ? Number(c.price) : 0 };
  }
 
  function photoList() { return state.placed.filter(p => p.kind === 'photo'); }
 
  function updateTotals() {
    const { total } = currentTotals();
    els.total.textContent = money(total);
    const n = state.placed.length;
    els.count.textContent = n + ' ' + (n === 1 ? (T.itemsOne || 'item placed') : (T.itemsMany || 'items placed'));
    els.atcPrice.textContent = money(total);
    els.atc.disabled = !(caseComplete() && dateOk());
    renderReview();
  }
 
  function renderReview() {
    if (!els.review) return;
    const { counts, casePrice } = currentTotals();
    const c = activeCase();
    let html = '';
    if (c) html += `<div class="sbb__review-row"><span>${c.wood} ${c.sizeLabel}${state.felt ? ', ' + state.felt + ' felt' : ''}</span><strong>${money(casePrice)}</strong></div>`;
    counts.forEach((qty, vid) => {
      const it = state.byVariant.get(vid);
      if (it) html += `<div class="sbb__review-row"><span>${qty > 1 ? qty + ' × ' : ''}${it.title}</span><strong>${money(it.price * qty)}</strong></div>`;
    });
    photoList().forEach(p => {
      html += `<div class="sbb__review-row"><span>${T.photoReview || 'Photo placeholder'} ${p.wIn}×${p.hIn} in${p.fileName ? ' — ' + p.fileName : ''}</span><strong>—</strong></div>`;
    });
    if (state.flag === 'add') html += `<div class="sbb__review-row"><span>${FLAG.addLabel || 'Folded flag'}</span><strong>${money(FLAG.price || 0)}</strong></div>`;
    else if (state.flag === 'own') html += `<div class="sbb__review-row"><span>${T.flagReviewOwn || 'Own flag'}</span><strong>—</strong></div>`;
    els.review.innerHTML = html || `<p>${T.reviewEmpty || ''}</p>`;
  }
 
  /* ── Engraving preview ──────────────────── */
  function bindEngraving() {
    [1, 2, 3].forEach(n => {
      const inp = $('#sbb-eng-' + n);
      if (!inp) return;
      inp.addEventListener('input', () => {
        $('#sbb-plate-' + n).textContent = inp.value;
        els.plate.hidden = ![1, 2, 3].some(m => $('#sbb-eng-' + m).value.trim());
      });
    });
  }
 
  /* ── Steps (gated: cannot leave step 1 until case complete) ─ */
  function gotoStep(step) {
    if (step !== 'case' && !caseComplete()) {
      if (els.lockNote) { els.lockNote.hidden = false; els.lockNote.classList.remove('is-flash'); void els.lockNote.offsetWidth; els.lockNote.classList.add('is-flash'); }
      step = 'case';
    }
    $$('.sbb__step').forEach(b => b.classList.toggle('is-active', b.dataset.step === step));
    $$('.sbb__panel-step').forEach(p => {
      const on = p.dataset.panel === step;
      p.classList.toggle('is-active', on);
      p.hidden = !on;
    });
    if (step === 'review') renderReview();
  }
 
  /* ── Cart ───────────────────────────── */
  async function addToCart() {
    const c = activeCase();
    if (!c) return;
    if (!dateOk()) {
      if (els.dateHint) els.dateHint.hidden = false;
      gotoStep('review');
      return;
    }
    els.atc.disabled = true;
    els.atc.textContent = T.atcAdding || 'Adding…';
 
    const { counts } = currentTotals();
    const photos = photoList().map(p => ({ wIn: p.wIn, hIn: p.hIn, x: Math.round(p.xPct * 10) / 10, y: Math.round(p.yPct * 10) / 10, z: p.z, f: p.fileName || '' }));
    const design = {
      v: 4,
      builderId: state.builderId,
      date: ceremonyDateText(),
      case: { variantId: c.variantId, wood: c.wood, size: c.sizeLabel, felt: state.felt || '' },
      items: state.placed.filter(p => p.kind === 'item').map(p => {
        const it = state.byVariant.get(String(p.variantId));
        return { vid: p.variantId, sku: it ? it.sku : '', cat: it ? it.cat : '', x: Math.round(p.xPct * 10) / 10, y: Math.round(p.yPct * 10) / 10, z: p.z };
      }),
      photos,
      flag: state.flag,
      engraving: [1, 2, 3].map(n => ($('#sbb-eng-' + n) || {}).value || '').filter(Boolean)
    };
 
    const placedSummary = [];
    counts.forEach((qty, vid) => {
      const it = state.byVariant.get(vid);
      if (it) placedSummary.push((qty > 1 ? qty + '× ' : '') + it.title);
    });
 
    const flagText = state.flag === 'add' ? (FLAG.addLabel || 'Folded flag') + ' (' + money(FLAG.price || 0) + ')'
      : state.flag === 'own' ? (T.flagReviewOwn || 'Customer supplying own flag') : 'None';
    // Name the actual file rather than a generic "(photo attached)". This property is public, so
    // it is the customer's receipt in the cart and at checkout that their upload made it through —
    // the CDN link itself rides the private _Photo file property and stays out of the cart display.
    const photoText = photos.length ? photos.map(p => p.wIn + '×' + p.hIn + ' in' + (p.f ? ' — ' + p.f : '')).join('; ') : 'None';
 
    const items = [{
      id: Number(c.variantId),
      quantity: 1,
      properties: {
        '_Builder ID': state.builderId,
        '_Design JSON': JSON.stringify(design),
        'Ceremony date': ceremonyDateText(),
        'Case': c.wood + ' ' + c.sizeLabel + (state.felt ? ', ' + state.felt + ' felt' : ''),
        'Placed items': placedSummary.join('; ') || 'None',
        'Photo placeholders': photoText,
        'Flag': flagText,
        'Engraving': design.engraving.join(' / ') || 'None'
      }
    }];
    counts.forEach((qty, vid) => {
      items.push({ id: Number(vid), quantity: qty, properties: { '_Builder ID': state.builderId } });
    });
    if (state.flag === 'add' && FLAG.variantId) {
      items.push({ id: Number(FLAG.variantId), quantity: 1, properties: { '_Builder ID': state.builderId } });
    }
 
    // Set once the multipart case line lands. The award lines have to go in a second request, so a
    // failure there would otherwise strand a lone case in the cart — and the customer's retry would
    // add a second one. Holding the line key lets the catch put the cart back how it found it.
    let caseLineKey = null;
 
    try {
      const photoFiles = photoList().filter(p => p.file);
      if (photoFiles.length) {
        // Multipart add for the case line so the customer's photo files upload with it.
        // Shopify stores each file and turns the property value into a CDN URL on the order.
        const fd = new FormData();
        fd.append('id', String(items[0].id));
        fd.append('quantity', '1');
        Object.keys(items[0].properties).forEach(k => fd.append('properties[' + k + ']', items[0].properties[k]));
        photoList().forEach((p, i) => {
          if (p.file) fd.append('properties[_Photo file ' + (i + 1) + ']', p.file, p.file.name);
        });
        const res1 = await fetch('/cart/add.js', { method: 'POST', body: fd });
        const data1 = await res1.json();
        if (!res1.ok) throw new Error(data1.description || 'Cart error');
        // Prefer the line key over the variant id: the cart can hold several lines of the same
        // case variant (different properties), and the key targets exactly the one we just added.
        caseLineKey = data1.key || null;
        const rest = items.slice(1);
        if (rest.length) {
          const res2 = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: rest })
          });
          if (!res2.ok) throw new Error((await res2.json()).description || 'Cart error');
        }
      } else {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error((await res.json()).description || 'Cart error');
      }
      window.location.href = '/cart';
    } catch (err) {
      // Roll the case line back out so the cart matches what the alert says (nothing was added),
      // and so pressing Add to cart again can't stack a duplicate case. Best effort: if the undo
      // itself fails there is nothing further we can do from here, and the cart page will show it.
      if (caseLineKey) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: caseLineKey, quantity: 0 })
          });
        } catch (e) { /* ignore — surfaced by the alert below */ }
      }
      els.atc.disabled = false;
      els.atc.innerHTML = (T.atcLabel || 'Add to cart —') + ' <span id="sbb-atc-price">' + els.total.textContent + '</span>';
      els.atcPrice = $('#sbb-atc-price');
      alert((T.cartError || 'Could not add to cart:') + ' ' + err.message);
    }
  }
 
  /* ── Wire up ──────────────────────────── */
  function init() {
    // Case display prices follow the live Shopify variant price (fall back to block setting)
    if (CFG.variantPrices) {
      CFG.cases.forEach(c => {
        const lp = CFG.variantPrices[c.variantId];
        if (lp != null) c.price = Number(lp);
      });
    }
    // NO pre-selection — customer must choose wood, size, and felt
    renderCaseOptions();
    renderFlagButtons();
    loadCatalog();
    bindEngraving();
    if (els.date) {
      els.date.min = new Date().toISOString().slice(0, 10);
      els.date.addEventListener('input', () => {
        if (els.dateHint) els.dateHint.hidden = true;
        updateTotals();
      });
    }
    if (els.noDate) {
      els.noDate.addEventListener('change', () => {
        if (els.date) {
          els.date.disabled = els.noDate.checked;
          if (els.noDate.checked) els.date.value = '';
        }
        if (els.dateHint) els.dateHint.hidden = true;
        updateTotals();
      });
    }
    if (els.stageHint) els.stageHint.hidden = false;
    $$('.sbb__step').forEach(b => b.addEventListener('click', () => gotoStep(b.dataset.step)));
    $$('.sbb__tab').forEach(b => b.addEventListener('click', () => {
      state.activeCat = b.dataset.cat;
      $$('.sbb__tab').forEach(x => x.classList.toggle('is-active', x === b));
      renderCatalog();
    }));
    $$('.sbb__chip').forEach(b => b.addEventListener('click', () => {
      state.activeBranch = b.dataset.branch;
      $$('.sbb__chip').forEach(x => x.classList.toggle('is-active', x === b));
      renderCatalog();
    }));
    let t;
    els.search.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(() => { state.search = els.search.value; renderCatalog(); }, 150);
    });
    if (els.photoAdd) els.photoAdd.addEventListener('click', addPhoto);
    if (els.flagChoices) {
      els.flagChoices.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-flag]');
        if (!btn) return;
        state.flag = (state.flag === btn.dataset.flag) ? null : btn.dataset.flag;
        renderFlagButtons();
        updateTotals();
      });
    }
    els.atc.addEventListener('click', addToCart);
    window.addEventListener('resize', resizePlaced);
  }
 
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
